const LEAFLET_CSS = [
  'https://cdn.jsdelivr.net/npm/leaflet@1.9.4/dist/leaflet.css',
  'https://cdn.jsdelivr.net/npm/leaflet-draw@1.0.4/dist/leaflet.draw.css',
]
const LEAFLET_CORE_JS = 'https://cdn.jsdelivr.net/npm/leaflet@1.9.4/dist/leaflet.js'
const LEAFLET_DRAW_JS = 'https://cdn.jsdelivr.net/npm/leaflet-draw@1.0.4/dist/leaflet.draw.js'
const GEOMETRYUTIL_JS = 'https://cdn.jsdelivr.net/npm/leaflet-geometryutil@0.10.3/src/leaflet.geometryutil.js'
const GOOGLE_MUTANT_JS = 'https://cdn.jsdelivr.net/npm/leaflet.gridlayer.googlemutant@0.13.5/dist/Leaflet.GoogleMutant.js'

let loadPromise = null
let gisStackPromise = null
function loadStylesheet(href) {
  if (document.querySelector(`link[href="${href}"]`)) {
    return Promise.resolve()
  }
  return new Promise((resolve, reject) => {
    const link = document.createElement('link')
    link.rel = 'stylesheet'
    link.href = href
    link.onload = () => resolve()
    link.onerror = () => reject(new Error(`Failed to load stylesheet: ${href}`))
    document.head.appendChild(link)
  })
}

function loadScript(src) {
  return new Promise((resolve, reject) => {
    const markLoaded = script => {
      script.setAttribute('data-loaded', 'true')
      resolve()
    }

    const existing = document.querySelector(`script[src="${src}"]`)
    if (existing) {
      if (existing.getAttribute('data-loaded') === 'true') {
        resolve()
        return
      }
      existing.addEventListener('load', () => markLoaded(existing), { once: true })
      existing.addEventListener('error', () => reject(new Error(`Failed to load script: ${src}`)), { once: true })
      return
    }

    const script = document.createElement('script')
    script.src = src
    script.async = false
    script.onload = () => markLoaded(script)
    script.onerror = () => reject(new Error(`Failed to load script: ${src}`))
    document.body.appendChild(script)
  })
}

/**
 * ArcGIS registers window.define (AMD). Leaflet UMD then binds to AMD instead of window.L.
 * Temporarily hide define so Leaflet attaches to the global.
 */
async function loadScriptAsGlobal(src) {
  const existing = document.querySelector(`script[src="${src}"]`)
  if (existing?.getAttribute('data-loaded') === 'true') {
    if (scriptReadyForSrc(src)) return
    existing.remove()
  }

  const previousDefine = window.define
  window.define = undefined
  try {
    await loadScript(src)
  } finally {
    window.define = previousDefine
  }
}

function scriptReadyForSrc(src) {
  if (src === LEAFLET_CORE_JS) return !!window.L
  if (src === LEAFLET_DRAW_JS) return isLeafletDrawReady()
  if (src === GEOMETRYUTIL_JS) return !!(window.L && window.L.GeometryUtil)
  if (src === GOOGLE_MUTANT_JS) return !!(window.L && window.L.gridLayer && window.L.gridLayer.googleMutant)
  return true
}

function isLeafletDrawReady() {
  return !!(window.L && window.L.Control && window.L.Control.Draw)
}

function isGisStackReady() {
  return isLeafletDrawReady()
    && !!(window.L?.GeometryUtil)
    && !!(window.L?.gridLayer?.googleMutant)
}

/**
 * Strips accidental inline comments from .env values (e.g. "AIza...  // demo").
 */
export function sanitizeGoogleMapsApiKey(raw) {
  if (!raw || typeof raw !== 'string') return ''
  return raw.replace(/\s*\/\/.*$/, '').trim().replace(/\s+/g, '')
}

/**
 * Google Maps API key: set VUE_APP_GOOGLE_MAPS_API_KEY in .env / .env.local
 */
export function getGoogleMapsApiKey() {
  const raw = process.env.VUE_APP_GOOGLE_MAPS_API_KEY
    || window.VUE_APP_GOOGLE_MAPS_API_KEY
    || ''
  return sanitizeGoogleMapsApiKey(raw)
}

let googleMapsBootstrapPromise = null

/**
 * Loads Google Maps JS API with loading=async (recommended bootstrap).
 * Use google.maps.importLibrary('maps' | 'geometry' | ...) after this resolves.
 */
export function loadGoogleMapsScript(apiKey = getGoogleMapsApiKey()) {
  const cleanKey = sanitizeGoogleMapsApiKey(apiKey)
  if (!cleanKey) {
    return Promise.reject(new Error('Google Maps API key is not configured (VUE_APP_GOOGLE_MAPS_API_KEY).'))
  }

  if (window.google?.maps?.importLibrary) {
    return Promise.resolve()
  }

  if (googleMapsBootstrapPromise) {
    return googleMapsBootstrapPromise
  }

  googleMapsBootstrapPromise = new Promise((resolve, reject) => {
    const callbackName = '__elhalawanyGoogleMapsReady'
    const finish = () => {
      delete window[callbackName]
      if (window.google?.maps) {
        resolve()
      } else {
        reject(new Error('Google Maps JavaScript API loaded but google.maps is unavailable.'))
      }
    }

    window[callbackName] = finish

    const src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(cleanKey)}&v=weekly&loading=async&callback=${callbackName}`
    const existing = document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]')

    if (existing) {
      let existingKey = ''
      try {
        existingKey = new URL(existing.src, window.location.origin).searchParams.get('key') || ''
      } catch (error) {
        existingKey = ''
      }

      if (existingKey && existingKey !== cleanKey) {
        existing.remove()
      } else if (window.google?.maps) {
        finish()
        return
      } else if (existingKey === cleanKey) {
        existing.addEventListener('load', finish, { once: true })
        existing.addEventListener('error', () => reject(new Error('Failed to load Google Maps JavaScript API')), { once: true })
        return
      }
    }

    const script = document.createElement('script')
    script.src = src
    script.async = true
    script.defer = true
    script.onerror = () => {
      delete window[callbackName]
      reject(new Error('Failed to load Google Maps JavaScript API'))
    }
    document.head.appendChild(script)
  }).catch(err => {
    googleMapsBootstrapPromise = null
    throw err
  })

  return googleMapsBootstrapPromise
}

/**
 * @param {'maps'|'geometry'|'marker'|'places'} name
 */
export async function importGoogleMapsLibrary(name) {
  await loadGoogleMapsScript()
  return window.google.maps.importLibrary(name)
}

/**
 * Loads Leaflet core + Leaflet.draw on window.L (CDN, AMD-safe).
 */
export function loadLeafletAssets() {
  if (isLeafletDrawReady()) {
    return Promise.resolve(window.L)
  }

  if (!loadPromise) {
    loadPromise = (async () => {
      try {
        await Promise.all(LEAFLET_CSS.map(loadStylesheet))

        await loadScriptAsGlobal(LEAFLET_CORE_JS)
        if (!window.L) {
          throw new Error('Leaflet core loaded but window.L is missing (AMD conflict?)')
        }

        await loadScriptAsGlobal(LEAFLET_DRAW_JS)
        if (!isLeafletDrawReady()) {
          throw new Error('Leaflet.draw loaded but L.Control.Draw is missing')
        }

        return window.L
      } catch (error) {
        loadPromise = null
        throw error
      }
    })()
  }

  return loadPromise
}

/**
 * Leaflet + draw + geometryutil + snap + Google Mutant (CDN globals).
 */
export function loadLeafletGisStack() {
  if (isGisStackReady()) {
    return Promise.resolve(window.L)
  }

  if (!gisStackPromise) {
    gisStackPromise = (async () => {
      try {
        const L = await loadLeafletAssets()
        await loadScriptAsGlobal(GEOMETRYUTIL_JS)
        await loadScriptAsGlobal(GOOGLE_MUTANT_JS)

        if (!isGisStackReady()) {
          throw new Error('Leaflet GIS stack loaded but required plugins are missing')
        }

        return L
      } catch (error) {
        gisStackPromise = null
        throw error
      }
    })()
  }

  return gisStackPromise
}
